/*
 * Orbital Timeline
 *
 * One SVG is measured against an independent article-content target. Every
 * H2 contributes one SVG node positioned on a single continuous path, plus
 * one final node marking the end of the article.
 *
 * The path's visible length tracks the reading position (the middle of the
 * viewport) so the line grows at exactly the pace of the scroll, and
 * retracts again when scrolling back up. Nodes illuminate as the line
 * reaches them and pulse once on first arrival.
 */
(function () {
  'use strict';

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var TRACK_X = 20;
  var REQUIRED_GUTTER = 88;
  var PULSE_DURATION = 1000;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function OrbitalTimeline(root) {
    this.root = root;
    this.target = document.getElementById(root.dataset.targetId);
    this.svg = root.querySelector('.orbital-timeline__svg');
    this.path = root.querySelector('[data-orbital-path]');
    this.lineGradient = root.querySelector('[data-orbital-line-gradient]');
    this.nodesLayer = root.querySelector('[data-orbital-nodes]');
    this.template = root.querySelector('[data-orbital-node-template]');
    this.headings = this.target
      ? Array.prototype.slice.call(this.target.querySelectorAll('h2'))
      : [];
    this.nodes = [];
    this.positions = [];
    this.litIndex = 0; // index of the furthest node the line currently reaches
    this.ticking = false;

    if (!this.target) {
      this.root.classList.add('orbital-timeline--hidden');
      return;
    }

    this.buildNodes();
    this.measure();
    this.applyStates(0, false);

    var self = this;

    this.onScroll = function () {
      if (self.ticking) return;
      self.ticking = true;
      window.requestAnimationFrame(function () {
        self.ticking = false;
        self.update();
      });
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.measure();
        self.update();
      });
      this.resizeObserver.observe(this.target);
    } else {
      window.addEventListener('resize', function () {
        self.measure();
        self.update();
      });
    }
  }

  OrbitalTimeline.prototype.buildNodes = function () {
    // One node per H2, plus a terminal node at the end.
    // With no headings, still draw start + end so every article gets a rail.
    var count = this.headings.length > 0 ? this.headings.length + 1 : 2;

    Array.apply(null, new Array(count)).forEach(
      function () {
        var node = document.createElementNS(SVG_NS, 'g');
        var visual = this.template.cloneNode(true);

        node.setAttribute('class', 'orbital-timeline__node');
        visual.removeAttribute('data-orbital-node-template');
        visual.setAttribute('class', 'orbital-timeline__node-visual');
        node.appendChild(visual);
        this.nodesLayer.appendChild(node);
        this.nodes.push(node);
      }.bind(this)
    );
  };

  /*
   * A node must sit on the heading text itself. The H2 box also contains
   * the decorative divider rendered by its ::before (plus that divider's
   * margin), so centring on the whole box lands the marker above the words.
   * Measuring the heading's DOM contents with a Range skips pseudo-elements
   * and yields the first text line, whose centre is the anchor — stable
   * even when a heading wraps onto several lines.
   */
  function headingAnchor(heading, targetRect) {
    var range = document.createRange();
    range.selectNodeContents(heading);

    var rects = range.getClientRects();
    range.detach();

    if (rects.length > 0) {
      var line = rects[0];
      return line.top - targetRect.top + line.height / 2;
    }

    var headingRect = heading.getBoundingClientRect();
    return headingRect.top - targetRect.top + headingRect.height / 2;
  }

  OrbitalTimeline.prototype.measure = function () {
    var targetRect = this.target.getBoundingClientRect();

    this.root.classList.toggle('orbital-timeline--hidden', targetRect.left < REQUIRED_GUTTER);

    this.positions = this.headings.map(function (heading) {
      return headingAnchor(heading, targetRect);
    });

    var height = Math.max(Math.ceil(this.target.offsetHeight), 1);

    // No H2s: start near the top of the content so the rail still draws
    if (this.positions.length === 0) {
      this.positions.push(24);
    }

    var first = this.positions[0];

    // The path runs from the first node to the very end of the article,
    // where the terminal node sits
    this.endY = Math.max(height - 8, first + 1);
    this.positions.push(this.endY);

    this.svg.setAttribute('height', String(height));
    this.svg.setAttribute('viewBox', '0 0 40 ' + height);
    this.path.setAttribute('d', 'M ' + TRACK_X + ' ' + first + ' V ' + this.endY);
    this.lineGradient.setAttribute('y1', String(first));
    this.lineGradient.setAttribute('y2', String(this.endY));

    this.nodes.forEach(
      function (node, index) {
        node.setAttribute('transform', 'translate(' + TRACK_X + ' ' + this.positions[index] + ')');
      }.bind(this)
    );
  };

  /*
   * The line tip follows the reading position: the point of the content
   * that currently sits in the middle of the viewport. This keeps the
   * drawing visible in the window and ties its pace directly to scrolling.
   */
  OrbitalTimeline.prototype.update = function () {
    if (this.positions.length === 0) return;

    var first = this.positions[0];
    var total = Math.max(this.endY - first, 1);

    var targetTop = this.target.getBoundingClientRect().top;
    var tip = window.innerHeight * 0.5 - targetTop;

    // At the very bottom of the page the viewport middle may never reach
    // the end of the article, so snap the tip to the terminal node
    var scrollBottom = window.scrollY + window.innerHeight;
    if (scrollBottom >= document.documentElement.scrollHeight - 2) {
      tip = this.endY;
    }

    tip = Math.min(Math.max(tip, first), this.endY);

    this.path.style.strokeDashoffset = String(1 - (tip - first) / total);

    // Furthest node the line currently reaches (the first is always lit)
    var lit = 0;
    for (var i = 1; i < this.positions.length; i += 1) {
      if (tip >= this.positions[i] - 1) lit = i;
    }

    if (lit !== this.litIndex) {
      this.applyStates(lit, lit > this.litIndex);
      this.litIndex = lit;
    }
  };

  OrbitalTimeline.prototype.applyStates = function (lit, arriving) {
    this.nodes.forEach(
      function (node, index) {
        node.classList.remove('is-active', 'is-passed');

        if (index < lit) {
          node.classList.add('is-passed');
        } else if (index === lit) {
          node.classList.add('is-active');

          if (arriving && !reducedMotion.matches) {
            node.classList.add('is-arriving');
            window.setTimeout(function () {
              node.classList.remove('is-arriving');
            }, PULSE_DURATION);
          }
        }
      }.bind(this)
    );
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-orbital-timeline]');

    Array.prototype.forEach.call(roots, function (root) {
      if (root.orbitalTimeline) return;
      root.orbitalTimeline = new OrbitalTimeline(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
